import axios from 'axios';
import { sanitizeInput } from './validationUtils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Get work experience directly from user database with course details
export const getWorkExperienceData = async () => {
  const token = localStorage.getItem('token');
  try {
    const response = await axios.get(`${API_BASE_URL}/get-work-experience-direct`, {
      headers: {
        'Authorization': `${token}`,
      },
    });
    
    // Check if the response has the expected structure
    if (response.data && response.data.error === false && response.data.result) {
      const data = response.data.result; // Direct access to result, not result.data
      return data;
    } else {
      throw new Error('Invalid response from API');
    }
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error fetching work experience");
  }
};

// Updated addWorkExperience function - replace the existing one
export const addWorkExperience = async (data) => {
  const token = localStorage.getItem('token');
  try {
    // Validate and sanitize input data
    const sanitizedData = {};

    // CourseId (optional - can be null for general work experience)
    if (data.courseId) {
      sanitizedData.courseId = data.courseId;
    } else {
      sanitizedData.courseId = null;
    }

    // Hours (optional) - 0 or blank is fine
    const parsedHours = parseFloat(data.hours);
    sanitizedData.hours = (!isNaN(parsedHours) && parsedHours > 0) ? parsedHours : 0;

    // Title (optional)
    if (data.title && data.title.trim()) {
      sanitizedData.title = sanitizeInput.text(data.title.trim());
    }

    // Comment (required)
    if (data.comment && data.comment.trim()) {
      sanitizedData.comment = sanitizeInput.text(data.comment.trim());
    } else {
      throw new Error('Comment is required');
    }

    // Optional date fields (YYYY-MM-DD)
    const validateDate = (val) => typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val) && !isNaN(new Date(val).getTime());
    if (data.date && validateDate(data.date)) sanitizedData.date = data.date;
    if (data.startDate && validateDate(data.startDate)) sanitizedData.startDate = data.startDate;
    if (data.endDate && validateDate(data.endDate)) sanitizedData.endDate = data.endDate;

    if (data.companyName) {
      sanitizedData.companyName = sanitizeInput.text(data.companyName);
    }
    
    if (data.supervisor) {
      sanitizedData.supervisor = sanitizeInput.text(data.supervisor);
    }
    
    if (data.tasks) {
      sanitizedData.tasks = sanitizeInput.text(data.tasks);
    }
    
    // Media array (optional)
    if (data.media && Array.isArray(data.media)) {
      sanitizedData.media = data.media;
    }
    
    const response = await axios.post(`${API_BASE_URL}/work-experience`, sanitizedData, {
      headers: {
        'Authorization': `${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    // Updated response validation - handle different possible response structures
    if (response.data) {
      // Check for success indicators in various formats
      const isSuccess = response.data.status === true || 
                       response.data.success === true || 
                       response.data.error === false ||
                       response.status === 201 ||
                       (response.data.message && !response.data.error);
      
      if (isSuccess) {
        return response.data;
      } else {
        // If we have specific error message, use it
        const errorMessage = response.data.error || 
                           response.data.message || 
                           'Failed to add work experience';
        throw new Error(errorMessage);
      }
    } else {
      throw new Error('Invalid response from server');
    }
  } catch (error) {
    console.error('Full error details:', error); // Debug log
    
    // If it's an axios error, check for response data
    if (error.response) {
      console.error('Error response data:', error.response.data); // Debug log
      const errorMessage = error.response.data?.error || 
                          error.response.data?.message || 
                          error.message || 
                          "Error adding work experience";
      throw new Error(errorMessage);
    } else {
      // Network error or other issues
      throw new Error(error.message || "Network error while adding work experience");
    }
  }
};

// Update existing work experience log entry using new direct API
export const updateWorkExperience = async (data) => {
  const token = localStorage.getItem('token');
  try {
    // Validate and sanitize input data
    const sanitizedData = {};

    // ID field (required for updates)
    if (data.id) {
      sanitizedData.id = data.id;
    } else {
      throw new Error('ID is required for updating work experience');
    }

    // CourseId (optional - can be null for general work experience)
    if (data.courseId) {
      sanitizedData.courseId = data.courseId;
    } else {
      sanitizedData.courseId = null;
    }

    // Hours (optional) - 0 or blank is fine
    const parsedUpdateHours = parseFloat(data.hours);
    sanitizedData.hours = (!isNaN(parsedUpdateHours) && parsedUpdateHours > 0) ? parsedUpdateHours : 0;
    
    // Title (optional)
    if (data.title !== undefined) {
      sanitizedData.title = data.title ? sanitizeInput.text(data.title.trim()) : null;
    }

    // Comment (required)
    if (data.comment && data.comment.trim()) {
      sanitizedData.comment = sanitizeInput.text(data.comment.trim());
    } else {
      throw new Error('Comment is required');
    }

    // Optional date fields (YYYY-MM-DD)
    const validateDate = (val) => typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val) && !isNaN(new Date(val).getTime());
    if (data.date !== undefined) sanitizedData.date = validateDate(data.date) ? data.date : null;
    if (data.startDate !== undefined) sanitizedData.startDate = validateDate(data.startDate) ? data.startDate : null;
    if (data.endDate !== undefined) sanitizedData.endDate = validateDate(data.endDate) ? data.endDate : null;

    if (data.companyName) {
      sanitizedData.companyName = sanitizeInput.text(data.companyName);
    }
    
    if (data.supervisor) {
      sanitizedData.supervisor = sanitizeInput.text(data.supervisor);
    }
    
    if (data.tasks) {
      sanitizedData.tasks = sanitizeInput.text(data.tasks);
    }
    
    // Media array (optional)
    if (data.media && Array.isArray(data.media)) {
      sanitizedData.media = data.media;
    }
    
    // Copy any other safe fields that don't need validation
    ['userId', 'createdAt', 'updatedAt'].forEach(field => {
      if (data[field] !== undefined) {
        sanitizedData[field] = data[field];
      }
    });
    
    const response = await axios.put(`${API_BASE_URL}/update-work-experience-direct`, sanitizedData, {
      headers: {
        'Authorization': `${token}`,
        'Content-Type': 'application/json',
      },
    });
    
    // Check if the response has the expected structure
    if (response.data && response.data.error === false) {
      return response.data;
    } else {
      throw new Error(response.data?.message || 'Failed to update work experience');
    }
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error updating work experience");
  }
};

// Delete work experience log entry using new direct API
export const deleteWorkExperience = async (logId) => {
  const token = localStorage.getItem('token');
  try {
    if (!logId) {
      throw new Error('Log ID is required for deletion');
    }

    // Use query parameter instead of request body for DELETE request
    const response = await axios.delete(`${API_BASE_URL}/delete-work-experience-direct?id=${logId}`, {
      headers: {
        'Authorization': `${token}`,
      },
    });
    
    // Check if the response has the expected structure
    if (response.data && response.data.error === false) {
      return response.data;
    } else {
      throw new Error(response.data?.message || 'Failed to delete work experience');
    }
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error deleting work experience");
  }
};